import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, RefreshCw, Phone, Clock, ShoppingBag, LogOut, Star, Check, EyeOff, Trash2, MessageSquare, Image as ImageIcon, UploadCloud, Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { MenuManager } from "@/components/MenuManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const KEY = "kitb_admin_pass";

export const OrdersAdmin = () => {
  const [pass, setPass] = useState(() => localStorage.getItem(KEY) || "");
  const [input, setInput] = useState("");
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState("orders");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [authed, setAuthed] = useState(false);

  const headers = (code) => ({ headers: { "X-Admin-Passcode": code } });

  const load = async (code) => {
    setLoading(true);
    setError("");
    try {
      const [ord, rev] = await Promise.all([
        axios.get(`${API}/orders`, headers(code)),
        axios.get(`${API}/admin/reviews`, headers(code)),
      ]);
      setOrders(ord.data);
      setReviews(rev.data);
      try {
        const gal = await axios.get(`${API}/admin/gallery`, headers(code));
        setGallery(gal.data);
      } catch (ge) {
        if (process.env.NODE_ENV === "development") console.error("Gallery load failed:", ge);
      }
      setAuthed(true);
      localStorage.setItem(KEY, code);
      setPass(code);
    } catch (e) {
      if (e?.response?.status === 401) {
        setError("Wrong passcode. Please try again.");
        setAuthed(false);
        localStorage.removeItem(KEY);
      } else {
        if (process.env.NODE_ENV === "development") console.error("Admin load failed:", e);
        setError("Could not load data. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pass) load(pass);
    // eslint-disable-next-line
  }, []);

  const prevCount = useRef(null);
  useEffect(() => {
    if (!authed) return;
    const beep = () => {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.type = "sine"; o.frequency.value = 880;
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
        o.start(); o.stop(ctx.currentTime + 0.5);
      } catch { /* audio blocked */ }
    };
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/orders`, headers(pass));
        if (prevCount.current !== null && data.length > prevCount.current) {
          beep();
          toast.success("🔔 New order received!", { duration: 8000 });
          setOrders(data);
        }
        prevCount.current = data.length;
      } catch { /* ignore poll errors */ }
    };
    prevCount.current = orders.length;
    const iv = setInterval(poll, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, pass]);

  const logout = () => {
    localStorage.removeItem(KEY);
    setPass("");
    setAuthed(false);
    setOrders([]);
    setReviews([]);
    setGallery([]);
  };

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await axios.post(`${API}/admin/gallery`, fd, {
          headers: { "X-Admin-Passcode": pass, "Content-Type": "multipart/form-data" },
        });
        setGallery((g) => [{ id: data.id, url: data.url, caption: data.caption }, ...g]);
      }
      toast.success(files.length > 1 ? `${files.length} photos posted to the feed!` : "Photo posted to the feed!");
    } catch {
      toast.error("Upload failed. Please try a smaller image or try again.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const deletePhoto = async (id) => {
    if (!window.confirm("Remove this photo from the feed?")) return;
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, headers(pass));
      setGallery((g) => g.filter((x) => x.id !== id));
      toast.success("Photo removed.");
    } catch { toast.error("Could not remove. Try again."); }
  };

  const approve = async (id) => {
    try {
      await axios.post(`${API}/admin/reviews/${id}/approve`, {}, headers(pass));
      setReviews((r) => r.map((x) => (x.id === id ? { ...x, approved: true } : x)));
      toast.success("Review approved — now visible on the site.");
    } catch { toast.error("Could not approve. Try again."); }
  };

  const hide = async (id) => {
    try {
      await axios.post(`${API}/admin/reviews/${id}/hide`, {}, headers(pass));
      setReviews((r) => r.map((x) => (x.id === id ? { ...x, approved: false } : x)));
      toast.success("Review hidden from the site.");
    } catch { toast.error("Could not hide. Try again."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, headers(pass));
      setReviews((r) => r.filter((x) => x.id !== id));
      toast.success("Review deleted.");
    } catch { toast.error("Could not delete. Try again."); }
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const pendingCount = reviews.filter((r) => !r.approved).length;

  if (!authed) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4">
        <Toaster position="top-center" richColors />
        <div className="bg-white rounded-3xl border border-line shadow-sm p-8 w-full max-w-sm text-center" data-testid="admin-login">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-brand/10 flex items-center justify-center text-brand">
            <Lock size={26} />
          </div>
          <h1 className="mt-5 font-serif text-2xl font-bold text-ink">KitchenBox Admin</h1>
          <p className="mt-2 text-sm text-inkmuted">Enter your passcode to manage orders & reviews.</p>
          <form onSubmit={(e) => { e.preventDefault(); load(input); }} className="mt-6 space-y-3">
            <input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              data-testid="admin-pass-input"
              placeholder="Passcode"
              className="w-full h-12 rounded-lg border border-line px-4 focus:border-brand focus:outline-none bg-white"
            />
            <button type="submit" disabled={loading} data-testid="admin-login-btn" className="w-full rounded-full bg-brand text-white py-3 font-bold hover:bg-brand-hover transition-colors disabled:opacity-60">
              {loading ? "Checking…" : "Log in"}
            </button>
          </form>
          {error && <p className="mt-3 text-sm text-red-600" data-testid="admin-error">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <Toaster position="top-center" richColors />
      <header className="bg-ink text-cream">
        <div className="max-w-5xl mx-auto px-4 py-5 flex items-center justify-between">
          <div>
            <h1 className="font-serif text-2xl font-bold text-white">KitchenBox — Admin</h1>
            <p className="text-cream/70 text-sm">{orders.length} order{orders.length !== 1 ? "s" : ""} · {reviews.length} review{reviews.length !== 1 ? "s" : ""}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => load(pass)} data-testid="admin-refresh" className="inline-flex items-center gap-2 bg-brand text-white rounded-full px-4 py-2 text-sm font-bold hover:bg-brand-hover transition-colors">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={logout} className="inline-flex items-center gap-2 bg-white/10 text-white rounded-full px-4 py-2 text-sm font-bold hover:bg-white/20 transition-colors">
              <LogOut size={16} /> Log out
            </button>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-4 flex gap-2">
          <button onClick={() => setTab("orders")} data-testid="admin-tab-orders" className={`px-4 py-2.5 text-sm font-bold rounded-t-lg transition-colors ${tab === "orders" ? "bg-cream text-ink" : "text-cream/70 hover:text-white"}`}>
            <ShoppingBag size={15} className="inline mr-1.5 -mt-0.5" /> Orders
          </button>
          <button onClick={() => setTab("reviews")} data-testid="admin-tab-reviews" className={`px-4 py-2.5 text-sm font-bold rounded-t-lg transition-colors ${tab === "reviews" ? "bg-cream text-ink" : "text-cream/70 hover:text-white"}`}>
            <MessageSquare size={15} className="inline mr-1.5 -mt-0.5" /> Reviews
            {pendingCount > 0 && <span className="ml-2 bg-gold text-ink text-xs font-bold px-2 py-0.5 rounded-full">{pendingCount} new</span>}
          </button>
          <button onClick={() => setTab("feed")} data-testid="admin-tab-feed" className={`px-4 py-2.5 text-sm font-bold rounded-t-lg transition-colors ${tab === "feed" ? "bg-cream text-ink" : "text-cream/70 hover:text-white"}`}>
            <ImageIcon size={15} className="inline mr-1.5 -mt-0.5" /> Feed
          </button>
          <button onClick={() => setTab("menu")} data-testid="admin-tab-menu" className={`px-4 py-2.5 text-sm font-bold rounded-t-lg transition-colors ${tab === "menu" ? "bg-cream text-ink" : "text-cream/70 hover:text-white"}`}>
            <ShoppingBag size={15} className="inline mr-1.5 -mt-0.5" /> Menu
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {tab === "orders" && (
          orders.length === 0 ? (
            <div className="text-center py-20 text-inkmuted">
              <ShoppingBag size={40} className="mx-auto text-line" />
              <p className="mt-3 font-semibold">No orders yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((o) => (
                <div key={o.id} data-testid={`admin-order-${o.id}`} className="bg-white rounded-2xl border border-line p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="font-serif text-lg font-bold text-ink">{o.name}</h3>
                      <a href={`tel:${o.phone}`} className="inline-flex items-center gap-1.5 text-sm text-brand font-semibold mt-0.5">
                        <Phone size={14} /> {o.phone}
                      </a>
                    </div>
                    <div className="text-right">
                      <span className="inline-block bg-gold/20 text-ink text-xs font-bold px-3 py-1 rounded-full capitalize">{o.order_type}</span>
                      <p className="mt-1 font-serif text-lg font-bold text-brand">${Number(o.total || 0).toFixed(2)}</p>
                    </div>
                  </div>
                  <pre className="mt-3 text-sm text-ink whitespace-pre-wrap font-sans bg-surface2 rounded-lg p-3 border border-line">{o.items}</pre>
                  {o.pickup_time && <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-brand font-semibold"><Clock size={14} /> Pickup: {o.pickup_time}</p>}
                  {o.notes && <p className="mt-2 text-sm text-inkmuted"><span className="font-semibold">Notes:</span> {o.notes}</p>}
                  <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-inkmuted"><Clock size={13} /> {fmt(o.created_at)}</p>
                </div>
              ))}
            </div>
          )
        )}

        {tab === "reviews" && (
          reviews.length === 0 ? (
            <div className="text-center py-20 text-inkmuted">
              <MessageSquare size={40} className="mx-auto text-line" />
              <p className="mt-3 font-semibold">No reviews yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-inkmuted">New reviews stay hidden until you <span className="font-semibold text-ink">Approve</span> them. Delete removes them for good.</p>
              {reviews.map((r) => (
                <div key={r.id} data-testid={`admin-review-${r.id}`} className={`bg-white rounded-2xl border p-5 ${r.approved ? "border-line" : "border-gold/60 bg-gold/5"}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="font-serif text-lg font-bold text-ink">{r.name}</h3>
                      <div className="flex text-gold mt-1">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} size={15} fill={i < (r.rating || 5) ? "currentColor" : "none"} strokeWidth={i < (r.rating || 5) ? 0 : 1.5} className={i < (r.rating || 5) ? "" : "text-gold/30"} />
                        ))}
                      </div>
                    </div>
                    <span className={`inline-block text-xs font-bold px-3 py-1 rounded-full ${r.approved ? "bg-green-100 text-green-700" : "bg-gold/30 text-ink"}`}>
                      {r.approved ? "Live" : "Pending"}
                    </span>
                  </div>
                  <p className="mt-3 text-sm text-ink leading-relaxed">{r.text}</p>
                  <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-inkmuted"><Clock size={13} /> {fmt(r.created_at)}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {!r.approved ? (
                      <button onClick={() => approve(r.id)} data-testid={`review-approve-${r.id}`} className="inline-flex items-center gap-1.5 bg-green-600 text-white rounded-full px-4 py-2 text-sm font-bold hover:bg-green-700 transition-colors">
                        <Check size={15} /> Approve
                      </button>
                    ) : (
                      <button onClick={() => hide(r.id)} data-testid={`review-hide-${r.id}`} className="inline-flex items-center gap-1.5 bg-white border border-line text-ink rounded-full px-4 py-2 text-sm font-bold hover:bg-surface2 transition-colors">
                        <EyeOff size={15} /> Hide
                      </button>
                    )}
                    <button onClick={() => remove(r.id)} data-testid={`review-delete-${r.id}`} className="inline-flex items-center gap-1.5 bg-red-50 text-red-600 rounded-full px-4 py-2 text-sm font-bold hover:bg-red-100 transition-colors">
                      <Trash2 size={15} /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {tab === "feed" && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-inkmuted">Post photos to your website <span className="font-semibold text-ink">Feed</span>. They appear instantly for customers.</p>
              <label data-testid="feed-upload-label" className="inline-flex items-center gap-2 bg-brand text-white rounded-full px-5 py-2.5 text-sm font-bold hover:bg-brand-hover transition-colors cursor-pointer">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
                {uploading ? "Uploading…" : "Add photos"}
                <input type="file" accept="image/*" multiple hidden disabled={uploading} onChange={uploadPhotos} data-testid="feed-upload-input" />
              </label>
            </div>
            {gallery.length === 0 ? (
              <div className="text-center py-20 text-inkmuted">
                <ImageIcon size={40} className="mx-auto text-line" />
                <p className="mt-3 font-semibold">No photos yet — tap "Add photos" to post your first one.</p>
              </div>
            ) : (
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {gallery.map((p) => (
                  <div key={p.id} data-testid={`admin-feed-${p.id}`} className="relative aspect-square rounded-2xl overflow-hidden border border-line bg-white group">
                    <img src={`${BACKEND}${p.url}`} alt={p.caption || "feed"} className="w-full h-full object-cover" />
                    <button onClick={() => deletePhoto(p.id)} data-testid={`feed-delete-${p.id}`} className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-2 shadow-md opacity-90 hover:bg-red-700 transition-colors" aria-label="Delete photo">
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {tab === "menu" && <MenuManager pass={pass} />}
      </main>
    </div>
  );
};
